import {formatDate} from "@angular/common";
import {HistoryEventKind, LogEntryItem, UserActivitySave} from "@app/_models/api";
import {dayHeading, dayKey, parseUtc} from "@app/processing/request-history/request-history.helpers";
import {EdiFileSummaryBaseComponent} from "@app/processing/edi-file-summary-base/edi-file-summary-base.component";

const LOCALE = "en-US";

export type HistoryEvent = {
    correlationId: string;
    tableName: string;
    record: string;
    /** Identifies the record within its table when its display name alone may not. */
    recordKey?: string;
    occurredAt: string;
    userName: string;
    customerName: string | null;
    kind: HistoryEventKind;
    summary: string;
    rows: LogEntryItem[];
};

export type DayGroup<T> = {
    key: string;
    heading: string;
    items: T[];
};

export type HistorySave = {
    correlationId: string;
    occurredAt: string;
    userName: string;
    customerName: string | null;
    events: HistoryEvent[];
};

export type TableDisplay = {
    label: string;
    icon: string;
};

const TABLE_DISPLAY: Record<string, TableDisplay> = {
    FEATURE_FLAG: {label: "Feature flag", icon: "bi-flag"},
    FEATURE_FLAG_USER: {label: "Feature flag user", icon: "bi-person-gear"}
};

export function tableDisplay(tableName: string): TableDisplay {
    return TABLE_DISPLAY[tableName] ?? {
        label: tableName.charAt(0) + tableName.slice(1).toLowerCase().replaceAll("_", " "),
        icon: "bi-table"
    };
}

const KIND_CLASSES: Record<HistoryEventKind, {badge: string; callout: string}> = {
    Added: {badge: "text-bg-success", callout: "callout-success"},
    Changed: {badge: "text-bg-info", callout: "callout-info"},
    Removed: {badge: "text-bg-danger", callout: "callout-danger"}
};

export function kindBadgeClass(kind: HistoryEventKind): string {
    return (KIND_CLASSES[kind] ?? KIND_CLASSES.Changed).badge;
}

export function kindCalloutClass(kind: HistoryEventKind): string {
    return (KIND_CLASSES[kind] ?? KIND_CLASSES.Changed).callout;
}

export function eventKey(event: HistoryEvent): string {
    return `${event.correlationId}|${event.tableName}|${event.recordKey ?? event.record}`;
}

export function appendNewEvents(existing: HistoryEvent[], incoming: HistoryEvent[]): HistoryEvent[] {
    const seen = new Set(existing.map(eventKey));
    return [...existing, ...incoming.filter(event => !seen.has(eventKey(event)))];
}

export function toActivityEvents(saves: UserActivitySave[], userName: string): HistoryEvent[] {
    return saves.flatMap(save => save.changes.map((change): HistoryEvent => ({
        correlationId: save.correlationId,
        tableName: change.tableName,
        record: change.ownerName ? `${change.record} (${change.ownerName})` : change.record,
        recordKey: change.primaryKey,
        occurredAt: save.occurredAt,
        userName,
        customerName: save.customerName,
        kind: change.kind,
        summary: change.summary,
        rows: save.rows.filter(row => row.tableName === change.tableName && row.primaryKey === change.primaryKey)
    })));
}

export function buildEventsCsv(events: HistoryEvent[]): string {
    const header = ["When (local time)", "User", "Customer", "Change", "Table", "Record", "Summary", "Correlation ID"];
    const rows = events.map(event => [
        formatDate(parseUtc(event.occurredAt), "yyyy-MM-dd HH:mm:ss", LOCALE),
        event.userName,
        event.customerName ?? "",
        event.kind,
        tableDisplay(event.tableName).label,
        event.record,
        event.summary,
        event.correlationId
    ]);

    return [header, ...rows]
        .map(row => row.map(EdiFileSummaryBaseComponent.toCsvField).join(","))
        .join("\r\n");
}

export function formatTime(value: string): string {
    return formatDate(parseUtc(value), "hh:mm a", LOCALE);
}

function groupByDayOf<T extends {occurredAt: string}>(items: Iterable<T>): DayGroup<T>[] {
    const groups: DayGroup<T>[] = [];
    for (const item of items) {
        const key = dayKey(item.occurredAt);
        const last = groups.at(-1);
        if (last?.key === key) {
            last.items.push(item);
        } else {
            groups.push({key, heading: dayHeading(item.occurredAt), items: [item]});
        }
    }
    return groups;
}

export function groupByDay(events: HistoryEvent[]): DayGroup<HistoryEvent>[] {
    return groupByDayOf(events);
}

export function groupBySave(events: HistoryEvent[]): DayGroup<HistorySave>[] {
    const saves = new Map<string, HistorySave>();
    for (const event of events) {
        const save = saves.get(event.correlationId);
        if (save) {
            save.events.push(event);
            if (parseUtc(event.occurredAt) > parseUtc(save.occurredAt)) {
                save.occurredAt = event.occurredAt;
            }
        } else {
            saves.set(event.correlationId, {
                correlationId: event.correlationId,
                occurredAt: event.occurredAt,
                userName: event.userName,
                customerName: event.customerName,
                events: [event]
            });
        }
    }

    const newestFirst = [...saves.values()]
        .sort((a, b) => parseUtc(b.occurredAt).getTime() - parseUtc(a.occurredAt).getTime());
    return groupByDayOf(newestFirst);
}
